import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import InsightsIcon from '@mui/icons-material/Insights';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useNavigate } from 'react-router-dom';
import {
  fetchAllInvoices,
  summarizeInvoices,
  billingYearRange,
  currentMonth,
  getUnitBilling,
  type UnitBilling,
  type ThisMonthStatus,
  type PublicInvoice,
} from '../../lib/ops-api';
import { formatRupiah, formatDate } from '../../lib/format';
import { extractApiError } from '../../lib/api-client';
import { useT, type TFunc } from '../../i18n';

const rp = (cents: number) => `Rp ${formatRupiah(cents)}`;

/** Compact Indonesian amount for tight cells: 150rb, 1,5jt. Full value goes in tooltips. */
function compactRp(cents: number): string {
  const idr = Math.floor(cents / 100);
  if (idr >= 1_000_000) {
    const jt = idr / 1_000_000;
    return `${jt.toFixed(jt >= 10 ? 0 : 1).replace('.', ',')}jt`;
  }
  if (idr >= 1_000) return `${Math.round(idr / 1000)}rb`;
  return String(idr);
}

// status -> i18n key
const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'rpt.statusDraft',
  OPEN: 'rpt.statusOpen',
  PAID: 'rpt.statusPaid',
  OVERDUE: 'rpt.statusOverdue',
  VOID: 'rpt.statusVoid',
};

function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  return new Date(y, m - 1, 1).toLocaleDateString('id-ID', { month: 'short', year: 'numeric' });
}

export function ReportsPage() {
  const [tab, setTab] = useState(0);
  const navigate = useNavigate();
  const t = useT();
  return (
    <Stack spacing={3}>
      <div>
        <Typography variant="h4" fontWeight={600}>
          {t('rpt.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('rpt.subtitle')}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<NotificationsActiveIcon />}
          onClick={() => navigate('/reminders')}
          sx={{ mt: 1.5 }}
        >
          {t('rpt.sendReminders')}
        </Button>
      </div>
      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label={t('rpt.tabSummary')} />
        <Tab label={t('rpt.tabByUnit')} />
      </Tabs>
      {tab === 0 ? <SummaryTab /> : <ByUnitTab />}
    </Stack>
  );
}

type PeriodMode = 'all' | 'current' | 'select';

// Long month names (id-ID) for the month picker — matches monthLabel's locale.
const MONTH_NAMES = Array.from({ length: 12 }, (_, i) =>
  new Date(2000, i, 1).toLocaleDateString('id-ID', { month: 'long' }),
);

function SummaryTab() {
  const now = new Date();
  const t = useT();
  const [invoices, setInvoices] = useState<PublicInvoice[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<PeriodMode>('current');
  const [selYear, setSelYear] = useState<number>(now.getFullYear());
  const [selMonth, setSelMonth] = useState<number>(now.getMonth() + 1); // 1-12

  useEffect(() => {
    fetchAllInvoices()
      .then(setInvoices)
      .catch((e) => setError(extractApiError(e).message))
      .finally(() => setLoading(false));
  }, []);

  const years = useMemo(() => billingYearRange(invoices ?? []), [invoices]);
  const period =
    mode === 'all'
      ? 'all'
      : mode === 'current'
        ? currentMonth()
        : `${selYear}-${String(selMonth).padStart(2, '0')}`;
  const summary = useMemo(
    () => (invoices ? summarizeInvoices(invoices, period) : null),
    [invoices, period],
  );

  if (error) return <Alert severity="error">{error}</Alert>;
  if (loading)
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  if (!summary) return null;

  const periodLabel = period === 'all' ? t('rpt.periodAll') : monthLabel(period);

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          select
          size="small"
          label={t('rpt.period')}
          value={mode}
          onChange={(e) => setMode(e.target.value as PeriodMode)}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="all">{t('rpt.periodAll')}</MenuItem>
          <MenuItem value="current">{t('rpt.periodCurrent')}</MenuItem>
          <MenuItem value="select">{t('rpt.periodSelect')}</MenuItem>
        </TextField>
        {mode === 'select' ? (
          <>
            <TextField
              select
              size="small"
              label={t('rpt.year')}
              value={selYear}
              onChange={(e) => setSelYear(Number(e.target.value))}
              sx={{ minWidth: 110 }}
            >
              {years.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label={t('rpt.month')}
              value={selMonth}
              onChange={(e) => setSelMonth(Number(e.target.value))}
              sx={{ minWidth: 150 }}
            >
              {MONTH_NAMES.map((name, i) => (
                <MenuItem key={i + 1} value={i + 1}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
          </>
        ) : null}
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 2,
        }}
      >
        <StatCard label={t('rpt.totalInvoices')} value={String(summary.total)} />
        <StatCard label={t('rpt.collected')} value={rp(summary.collectedCents)} color="success.main" />
        <StatCard label={t('rpt.outstanding')} value={rp(summary.outstandingCents)} color="warning.main" />
        <StatCard label={t('rpt.collectionRate')} value={`${Math.round(summary.collectionRate * 100)}%`} />
      </Box>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            {t('rpt.collectionRate')}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.round(summary.collectionRate * 100)}
            sx={{ height: 10, borderRadius: 5 }}
            color="success"
          />
          <Typography variant="caption" color="text.secondary">
            {t('rpt.collectedOfBilled', {
              collected: rp(summary.collectedCents),
              billed: rp(summary.collectedCents + summary.outstandingCents),
              period: periodLabel,
            })}
          </Typography>
        </CardContent>
      </Card>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            {t('rpt.invoicesByStatus')}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {Object.keys(STATUS_LABELS).map((s) => (
              <Chip
                key={s}
                label={`${t(STATUS_LABELS[s])}: ${summary.byStatus[s] ?? 0}`}
                variant="outlined"
                color={s === 'PAID' ? 'success' : s === 'OVERDUE' ? 'error' : s === 'OPEN' ? 'warning' : 'default'}
              />
            ))}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}

interface MonthCell {
  ym: string; // "YYYY-MM"
  label: string; // short month, e.g. "Jun"
  status: ThisMonthStatus; // PAID | UNPAID | NONE
  amountCents: number; // sum of that month's invoice totals (0 if not billed)
  paidAt: string | null;
}

/** Trailing 12 months including the current one, oldest → newest. */
function buildYearStrip(unit: UnitBilling): MonthCell[] {
  const now = new Date();
  const cells: MonthCell[] = [];
  for (let k = 11; k >= 0; k--) {
    const d = new Date(now.getFullYear(), now.getMonth() - k, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const monthInvs = unit.invoices.filter((i) => i.issue_date.slice(0, 7) === ym);
    let status: ThisMonthStatus = 'NONE';
    if (monthInvs.length > 0) {
      status = monthInvs.some((i) => i.status === 'OPEN' || i.status === 'OVERDUE')
        ? 'UNPAID'
        : 'PAID';
    }
    const paid = monthInvs.find((i) => i.status === 'PAID' && i.paid_at);
    cells.push({
      ym,
      label: d.toLocaleDateString('id-ID', { month: 'short' }),
      status,
      amountCents: monthInvs.reduce((s, i) => s + i.total_cents, 0),
      paidAt: paid?.paid_at ?? null,
    });
  }
  return cells;
}

function cellSx(status: ThisMonthStatus) {
  if (status === 'PAID') return { bgcolor: 'success.light', color: 'success.dark', borderColor: 'success.main' };
  if (status === 'UNPAID') return { bgcolor: 'error.light', color: 'error.dark', borderColor: 'error.main' };
  return { bgcolor: 'grey.100', color: 'text.secondary', borderColor: 'divider' };
}

function cellTooltip(c: MonthCell, t: TFunc): string {
  const m = monthLabel(c.ym);
  if (c.status === 'NONE') return t('rpt.tipNotBilled', { month: m });
  if (c.status === 'PAID') {
    return c.paidAt
      ? t('rpt.tipPaidOn', { month: m, amount: rp(c.amountCents), date: formatDate(c.paidAt.slice(0, 10)) })
      : t('rpt.tipPaid', { month: m, amount: rp(c.amountCents) });
  }
  return t('rpt.tipUnpaid', { month: m, amount: rp(c.amountCents) });
}

function YearStrip({ unit }: { unit: UnitBilling }) {
  const cells = buildYearStrip(unit);
  const t = useT();
  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {t('rpt.last12Months', { unit: `${unit.block}-${unit.number}` })}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {cells.map((c) => (
          <Tooltip key={c.ym} title={cellTooltip(c, t)} arrow>
            <Box
              sx={{
                width: 60,
                py: 0.5,
                px: 0.25,
                textAlign: 'center',
                borderRadius: 1,
                border: '1px solid',
                ...cellSx(c.status),
              }}
            >
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, textTransform: 'capitalize' }}>
                {c.label}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', fontSize: 10 }}>
                {c.status === 'NONE' ? '—' : compactRp(c.amountCents)}
              </Typography>
            </Box>
          </Tooltip>
        ))}
      </Box>
      <Stack direction="row" spacing={2} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
        <LegendSwatch status="PAID" label={t('rpt.legendPaid')} />
        <LegendSwatch status="UNPAID" label={t('rpt.legendUnpaid')} />
        <LegendSwatch status="NONE" label={t('rpt.legendNotBilled')} />
      </Stack>
    </Box>
  );
}

function LegendSwatch({ status, label }: { status: ThisMonthStatus; label: string }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box sx={{ width: 14, height: 14, borderRadius: 0.5, border: '1px solid', ...cellSx(status) }} />
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}

function ByUnitTab() {
  const t = useT();
  const [rows, setRows] = useState<UnitBilling[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState<UnitBilling | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  useEffect(() => {
    getUnitBilling()
      .then(setRows)
      .catch((e) => setError(extractApiError(e).message))
      .finally(() => setLoading(false));
  }, []);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const sorted = rows
      .slice()
      .sort((a, b) => (a.block + a.number).localeCompare(b.block + b.number, undefined, { numeric: true }));
    if (!needle) return sorted;
    return sorted.filter((r) =>
      `${r.block}${r.number} ${r.block}-${r.number} ${r.label}`.toLowerCase().includes(needle),
    );
  }, [rows, q]);

  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Stack spacing={2}>
      <TextField
        size="small"
        label={t('rpt.findUnit')}
        placeholder={t('rpt.findUnitPlaceholder')}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        sx={{ maxWidth: 280 }}
      />
      <Typography variant="caption" color="text.secondary">
        {t('rpt.byUnitHelp')}
      </Typography>
      <Card variant="outlined">
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : shown.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('rpt.noUnitsMatch')}</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 48 }} />
                    <TableCell>{t('rpt.colUnit')}</TableCell>
                    <TableCell sx={{ width: 140 }}>{t('rpt.colThisMonth')}</TableCell>
                    <TableCell sx={{ width: 130 }}>{t('rpt.colMonthsBehind')}</TableCell>
                    <TableCell sx={{ width: 150 }}>{t('rpt.outstanding')}</TableCell>
                    <TableCell sx={{ width: 140 }}>{t('rpt.colOldestUnpaid')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shown.map((r) => {
                    const isOpen = expanded.has(r.unit_id);
                    return (
                      <Fragment key={r.unit_id}>
                        <TableRow
                          hover
                          sx={{ cursor: 'pointer', '& > *': { borderBottom: isOpen ? 'unset' : undefined } }}
                          onClick={() => toggleExpand(r.unit_id)}
                        >
                          <TableCell sx={{ width: 48 }}>
                            <IconButton size="small" aria-label={isOpen ? t('rpt.collapse') : t('rpt.expand')}>
                              <KeyboardArrowDownIcon
                                fontSize="small"
                                sx={{
                                  transition: 'transform 0.2s',
                                  transform: isOpen ? 'rotate(180deg)' : 'none',
                                }}
                              />
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Link
                              component="button"
                              type="button"
                              underline="hover"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDetail(r);
                              }}
                              sx={{ fontWeight: 700, color: 'inherit', textAlign: 'left' }}
                            >
                              {r.block}-{r.number}
                            </Link>
                            {r.label && r.label !== `${r.block}-${r.number}` ? (
                              <Typography component="span" variant="caption" color="text.secondary">
                                {' '}
                                · {r.label}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <ThisMonthChip status={r.thisMonth} />
                          </TableCell>
                          <TableCell>
                            {r.unpaidCount > 0 ? (
                              <Chip size="small" color="error" label={t('rpt.monthsBehindValue', { count: r.unpaidCount })} />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                {t('rpt.upToDate')}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{r.outstandingCents > 0 ? rp(r.outstandingCents) : '—'}</TableCell>
                          <TableCell>{r.oldestUnpaidMonth ? monthLabel(r.oldestUnpaidMonth) : '—'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ py: 0, borderBottom: isOpen ? undefined : 'none' }} colSpan={6}>
                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                              <YearStrip unit={r} />
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
      <UnitInvoicesDialog unit={detail} onClose={() => setDetail(null)} />
    </Stack>
  );
}

function ThisMonthChip({ status }: { status: ThisMonthStatus }) {
  const t = useT();
  if (status === 'PAID') return <Chip size="small" color="success" label={t('rpt.legendPaid')} />;
  if (status === 'UNPAID') return <Chip size="small" color="error" label={t('rpt.legendUnpaid')} />;
  return <Chip size="small" label={t('rpt.legendNotBilled')} />;
}

function UnitInvoicesDialog({ unit, onClose }: { unit: UnitBilling | null; onClose: () => void }) {
  const t = useT();
  return (
    <Dialog open={!!unit} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {unit ? t('rpt.unitInvoicesTitle', { unit: `${unit.block}-${unit.number}` }) : ''}
      </DialogTitle>
      <DialogContent>
        {unit ? (
          unit.invoices.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              {t('rpt.noUnitInvoices')}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('rpt.colMonth')}</TableCell>
                    <TableCell>{t('rpt.colAmount')}</TableCell>
                    <TableCell>{t('rpt.colStatus')}</TableCell>
                    <TableCell>{t('rpt.colPaidOn')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unit.invoices.map((inv: PublicInvoice) => (
                    <TableRow key={inv.id}>
                      <TableCell>{monthLabel(inv.issue_date.slice(0, 7))}</TableCell>
                      <TableCell>{rp(inv.total_cents)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={STATUS_LABELS[inv.status] ? t(STATUS_LABELS[inv.status]) : inv.status}
                          color={inv.status === 'PAID' ? 'success' : inv.status === 'OVERDUE' ? 'error' : inv.status === 'OPEN' ? 'warning' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{inv.paid_at ? formatDate(inv.paid_at.slice(0, 10)) : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: 'action.hover', color: 'secondary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
          <InsightsIcon fontSize="small" />
        </Box>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" fontWeight={600} sx={{ color: color ?? 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}
